import type { Annotation, AnnotationDeadband } from '@studio/design-system/video-annotation';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type {
  AutoTrackingState,
  SelectionBbox
} from '@tryiris-ai/common.protojs/autotracking/v2/state';
import {
  DetectionMode,
  FrameAlign,
  FrameZoom
} from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { calcAlignmentPt, calcDeadbandFromBbox, calcToleranceBbox } from './utils.js';
import { locateProxy } from '#~/agent/index.js';
import type {
  AutoTrackingAnalyticsPendingState,
  AutoTrackingAnalyticsSnapshot
} from '#~/auto-tracking/analytics.js';
import { deriveAutoTrackingLifecycleEvents } from '#~/auto-tracking/analytics.js';
import { useAutoTracking } from '#~/auto-tracking/hooks.js';
import type { AutoTrackingStateDeadband } from '#~/auto-tracking/index.js';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { EVENT_CATEGORIES } from '#~/analytics/constants/index.js';
import { track } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { hide, post } from '#~/notifications/index.js';
import { toHexFromId } from '#~/api/utils.js';

const redetectionTimeout = 15_000;
const redetectionActivationThreshold = 1_000;

interface Props {
  deviceId: Device['id'];
}

const lastKnownAnnotationsMap: Record<Device['id'], number> = {};

export let trackerCPUMode: 'none' | 'efftam' | 'mediapipe' | 'yolo' = 'yolo';

export const setTrackerCPUMode = (mode: 'none' | 'efftam' | 'mediapipe' | 'yolo'): void => {
  trackerCPUMode = mode;
};

/** @internal */
export type Wiring = Readonly<{
  annotations: T_Maybe<Annotation[]>;
  deadband: T_Maybe<AutoTrackingStateDeadband>;
  deadbandSafetyArea: boolean;
  detectionMode: T_Maybe<DetectionMode>;
  handleChangeToleranceDeadband: (toleranceDeadband: AnnotationDeadband) => void;

  handleCreateReservation: () => Promise<boolean>;
  handleDestroyReservation: (force: boolean) => void;

  handleSelectAnnotation: (subjectId: T_Maybe<Uint8Array>, bbox: number[]) => void;

  handleDrawBoundingBox: (bbox: number[]) => void;
  handleStopTrackingSelectedSubject: (notify?: boolean) => void;
  handleRequestTransferTracking: () => Promise<void>;
  hasAnnotations: boolean;
  hasAssignment: boolean;
  hasNoWorkers: boolean;
  hasReservation: boolean;
  isAnnotationSelected: boolean;
  isCompositionActive: boolean;
  isConnected: boolean;
  isRedetecting: boolean;
  isReturningToSafePosition: boolean;
  isTracking: boolean;
  isRestarting: boolean;
  trackingEngagedDeviceNames: string[];
  safePositionCountdown: number;
  selectedSubjectId: T_Maybe<Uint8Array>;
  wantsTracking: boolean;
  redetectionCountdown: number;
}>;

const lastKnownLocationsMap: Record<string, Annotation> = {};
const lastSeenAnnotationsMap: Record<Device['id'], Date> = {};
const redetectionTimeoutsMap: Record<Device['id'], NodeJS.Timeout> = {};
const redetectionIntervalsMap: Record<Device['id'], NodeJS.Timeout> = {};

/** @internal */
export const useWiring = ({
  agentId,
  deviceId
}: Props & {
  /** Agent ID. */
  agentId: Agent['id'];
}): Wiring => {
  const proxy = locateProxy(deviceId);

  const mutateAutoTrackingState = async (
    set: DeepPartial<AutoTrackingState>,
    unset: DeepPartial<AutoTrackingState>,
    optimistic: boolean = false
  ): Promise<void> => {
    const workerId = Object.entries(proxy?.state?.control?.studio?.autoTrackingWorker ?? {}).find(
      ([_, w]) => toHexFromId(w?.state?.control?.deviceId) === deviceId
    )?.[0];
    const workerIdHex = workerId ?? '';

    if (workerIdHex.length === 0) {
      console.warn('$MUTATE AutoTrackingState: No worker found for device', deviceId);
      return;
    }

    await proxy?.mutate(
      Object.entries(set).length > 0
        ? {
            control: {
              studio: {
                autoTrackingWorker: {
                  [workerIdHex]: {
                    state: set
                  }
                }
              }
            }
          }
        : {},
      Object.entries(unset).length > 0
        ? {
            control: {
              studio: {
                autoTrackingWorker: {
                  [workerIdHex]: {
                    state: unset
                  }
                }
              }
            }
          }
        : {},
      optimistic
    );
  };

  const [showingProvisioningNotification, setShowingProvisioningNotification] = useState(false);
  const autoTrackingLifecycleRef = useRef<AutoTrackingAnalyticsSnapshot | null>(null);
  const autoTrackingPendingRef = useRef<AutoTrackingAnalyticsPendingState>({});

  const [provisioningNotificationTimeout, setProvisioningNotificationTimeout] =
    useState<NodeJS.Timeout>();

  const { annotations, reservation, state } = useAutoTracking(agentId, deviceId) ?? {};

  const clearSubjectLostNotification = useCallback((): void => {
    hide(`autotracking.lost.${deviceId}`);
    hide(`autotracking.redetecting.${deviceId}`);
  }, [deviceId]);

  const clearAutoTrackingNotifications = useCallback((): void => {
    hide(`autotracking.provisioning.${deviceId}`);
    hide(`autotracking.stop.${deviceId}`);
    clearSubjectLostNotification();
  }, [deviceId, clearSubjectLostNotification]);

  const handleChangeToleranceDeadband = (newToleranceDeadband: AnnotationDeadband): void => {
    const currentDeadband = state?.deadBand;

    const oldToleranceDeadband = calcToleranceBbox(currentDeadband);
    const oldAlignmentPt = calcAlignmentPt(currentDeadband);

    const relativeAlignmentPt = {
      x: oldAlignmentPt.x - oldToleranceDeadband.x,
      y: oldAlignmentPt.y - oldToleranceDeadband.y
    };

    const scaleX = (newToleranceDeadband.width ?? 0) / oldToleranceDeadband.width;
    const scaleY = (newToleranceDeadband.height ?? 0) / oldToleranceDeadband.height;

    const newAlignmentPt = {
      x: (newToleranceDeadband.x ?? 0) + relativeAlignmentPt.x * scaleX,
      y: (newToleranceDeadband.y ?? 0) + relativeAlignmentPt.y * scaleY
    };

    mutateAutoTrackingState(
      {
        control: {
          deadBand: {
            /*
              width: deadband.width,
              height: deadband.height,
              x: deadband.x,
              y: deadband.y,
              */
            ...calcDeadbandFromBbox(
              currentDeadband?.hr ?? 0.05,
              newToleranceDeadband,
              newAlignmentPt
            )
          },
          frameAlign: FrameAlign.FRAME_ALIGN_NONE
        }
      },
      {},
      true
    ).catch(noop);
  };

  const handleSelectAnnotation = (id: T_Maybe<Uint8Array>, bbox: number[]): void => {
    autoTrackingPendingRef.current = {
      ...autoTrackingPendingRef.current,
      nextStartSurface: 'video_overlay',
      nextSubjectSource: 'manual_selection'
    };

    clearAutoTrackingNotifications();

    const control: DeepPartial<AutoTrackingState['control']> = {
      detectionMode: DetectionMode.DETECTION_MODE_TRACKING
    };

    if (id !== undefined) {
      control.selectedSubjectId = id;
      if (trackerCPUMode === 'efftam') {
        control.selectedSubjectId = new Uint8Array([1]);
      }

      control.selectionBbox = <SelectionBbox>{
        x1: bbox[0]!,
        y1: bbox[1]!,
        x2: bbox[2]!,
        y2: bbox[3]!,
        isHuman: true
      };
      (window as any).__myBoundingBoxRelative = bbox;
    }

    mutateAutoTrackingState(
      {
        control,
        status: {
          detectionMode: DetectionMode.DETECTION_MODE_TRACKING
        }
      },
      {}
    ).catch(noop);
  };

  const handleStopTrackingSelectedSubject = async (notify: boolean = true): Promise<void> => {
    autoTrackingPendingRef.current = {
      ...autoTrackingPendingRef.current,
      nextStopReason: 'user_disabled'
    };

    clearTimeout(redetectionTimeoutsMap[resKey]!);
    delete redetectionTimeoutsMap[resKey];

    clearInterval(redetectionIntervalsMap[resKey]!);
    delete redetectionIntervalsMap[resKey];

    await mutateAutoTrackingState(
      {
        control: {
          detectionMode: DetectionMode.DETECTION_MODE_SEARCHING,
          selectionBbox: { x1: 1, y1: 1, x2: 1, y2: 1, isHuman: true }
        },
        status: {
          detectionMode: DetectionMode.DETECTION_MODE_SEARCHING
        }
      },
      {
        control: {
          selectedSubjectId: new Uint8Array([1])
        }
      }
    );
    /*
    await mutateAutoTrackingState({}, {
      control: {
        selectedSubjectId: new Uint8Array([1]),
      }
    })
*/
    if (notify) {
      clearAutoTrackingNotifications();

      post({
        id: `autotracking.stop.${deviceId}`,
        title: 'Auto-Tracking Stopped',
        message: 'You have successfully stopped tracking.'
      });
    }
  };

  const hasReservation = reservation !== undefined;
  const hasAssignment =
    Object.values(proxy?.state?.control?.studio?.autoTrackingWorker ?? {}).find((worker) => {
      return (toHexFromId(worker?.state?.control?.deviceId) ?? '') === deviceId;
    }) !== undefined;

  const hasAnnotationsRaw = annotations?.length !== 0;

  if (hasAnnotationsRaw) {
    lastKnownAnnotationsMap[deviceId] = Date.now();
  }

  const hasAnnotations =
    hasAnnotationsRaw || Math.abs(Date.now() - (lastKnownAnnotationsMap[deviceId] ?? 0)) < 2000;

  let selectedAnnotation =
    (state?.selectedSubjectId !== undefined &&
      annotations?.find(
        (annotation) => annotation.id.toString() === state.selectedSubjectId?.toString()
      )) ||
    undefined;

  if (
    state?.selectedSubjectId?.length === 0 &&
    annotations?.length === 1 &&
    annotations[0]?.id.toString() === new Uint8Array([1]).toString()
  ) {
    selectedAnnotation = annotations[0];
  }

  const [redetectionCountdownSmooth, setRedetectionCountdownSmooth] = useState(0);

  const resKey = deviceId;

  if (selectedAnnotation !== undefined) {
    lastKnownLocationsMap[selectedAnnotation.id.toString()] = selectedAnnotation;

    lastSeenAnnotationsMap[resKey] = new Date();

    if (redetectionTimeoutsMap[resKey] !== undefined) {
      clearTimeout(redetectionTimeoutsMap[resKey]);
      delete redetectionTimeoutsMap[resKey];
    }

    redetectionTimeoutsMap[resKey] = setTimeout(() => {
      const worker = Object.values(proxy?.state?.control?.studio?.autoTrackingWorker ?? {}).find(
        (worker) => toHexFromId(worker?.state?.control?.deviceId) === deviceId
      );

      if (worker === undefined) {
        return;
      }

      handleStopTrackingSelectedSubject(true);
    }, redetectionTimeout);

    if (redetectionIntervalsMap[resKey] === undefined) {
      redetectionIntervalsMap[resKey] = setInterval(() => {
        const lastSeen = lastSeenAnnotationsMap[resKey];
        if (lastSeen !== undefined) {
          const timeSinceLastSeen = new Date().getTime() - lastSeen.getTime();
          const countdown = Math.max(0, Math.ceil((redetectionTimeout - timeSinceLastSeen) / 1000));
          setRedetectionCountdownSmooth(countdown);
        }
      }, 200);
    }
  }

  const isAnnotationSelected = selectedAnnotation !== undefined;

  let isTracking = state?.detectionMode === DetectionMode.DETECTION_MODE_TRACKING;

  const lastSeen = state?.selectedSubjectId ? lastSeenAnnotationsMap[resKey] : undefined;

  let isRedetecting =
    isTracking &&
    lastSeen !== undefined &&
    new Date().getTime() - lastSeen.getTime() > redetectionActivationThreshold;

  const hasLostSubject =
    isTracking &&
    lastSeen !== undefined &&
    new Date().getTime() - lastSeen.getTime() > redetectionTimeout;

  if (hasLostSubject) {
    isTracking = false;
    isRedetecting = false;
  }

  const isReturningToSafePosition =
    state?.status?.detectionMode === DetectionMode.DETECTION_MODE_RETURNING_TO_SAFE_POSITION;

  const detectionMode = isRedetecting
    ? DetectionMode.DETECTION_MODE_REDETECTING
    : state?.detectionMode;
  const wantsTracking = detectionMode === DetectionMode.DETECTION_MODE_TRACKING;

  const annotationId = state?.selectedSubjectId?.toString() ?? '';

  const lastKnownLocation = lastKnownLocationsMap[annotationId];

  const filteredAnnotations =
    isRedetecting && lastKnownLocation
      ? [
          {
            ...lastKnownLocation,
            color: 'error'
          }
        ]
      : isAnnotationSelected
        ? [
            {
              ...selectedAnnotation,
              color: 'green'
            }
          ]
        : annotations;

  useEffect(() => {
    const autoTrackingAnalyticsContext = resolveDeviceAnalyticsContext(deviceId);

    const nextSnapshot: AutoTrackingAnalyticsSnapshot = {
      hasAssignment,
      hasLostSubject,
      hasReservation,
      isAnnotationSelected,
      isRedetecting,
      isTracking
    };

    if (!autoTrackingAnalyticsContext) {
      autoTrackingLifecycleRef.current = nextSnapshot;
      return;
    }

    if (autoTrackingLifecycleRef.current === null) {
      autoTrackingLifecycleRef.current = nextSnapshot;
      return;
    }

    const { events, pending } = deriveAutoTrackingLifecycleEvents(
      autoTrackingLifecycleRef.current,
      nextSnapshot,
      autoTrackingAnalyticsContext,
      autoTrackingPendingRef.current
    );

    autoTrackingLifecycleRef.current = nextSnapshot;
    autoTrackingPendingRef.current = pending;

    events.forEach((event) => {
      void track({
        category: EVENT_CATEGORIES.AUTO_TRACKING,
        name: event.name,
        properties: event.properties
      } as AnalyticsEvent);
    });
  }, [
    deviceId,
    hasAssignment,
    hasLostSubject,
    hasReservation,
    isAnnotationSelected,
    isRedetecting,
    isTracking
  ]);
  /*
  useEffect(() => {
    if (
      state?.status?.detectionMode === DetectionMode.READY &&
      state.detectionMode === DetectionMode.TRACKING_SUBJECT
    ) {
      clearAutoTrackingNotifications();

      post({
        id: `autotracking.lost.${deviceId}`,
        title: 'Subject Lost',
        message: 'The subject you were tracking has been lost. Auto-Tracking has stopped.',
        severity: 'error'
      });
    }

    if (
      state?.status?.detectionMode === DetectionMode.REDETECTING &&
      state.detectionMode === DetectionMode.TRACKING_SUBJECT
    ) {
      clearAutoTrackingNotifications();

      post({
        id: `autotracking.redetecting.${deviceId}`,
        title: 'Subject Lost',
        message: 'The subject you were tracking has been lost. Redetection in progress...',
        severity: 'error'
      });
    }

    if (state?.status?.detectionMode === DetectionMode.TRACKING_SUBJECT) {
      clearSubjectLostNotification();
    }
  }, [
    state?.status?.detectionMode,
    state?.detectionMode,
    clearAutoTrackingNotifications,
    clearSubjectLostNotification,
    deviceId
  ]);
*/
  useEffect(() => {
    if ((reservation?.workerId?.length ?? 0) !== 0) {
      if (provisioningNotificationTimeout !== undefined) {
        clearTimeout(provisioningNotificationTimeout);
        setProvisioningNotificationTimeout(undefined);
      }

      if (showingProvisioningNotification) {
        hide(`autotracking.start.provisioning.${deviceId}`);

        setShowingProvisioningNotification(false);
      }
    }
  }, [
    deviceId,
    reservation?.workerId,
    provisioningNotificationTimeout,
    showingProvisioningNotification
  ]);

  const frameAlign = state?.frameAlign ?? FrameAlign.FRAME_ALIGN_NONE;
  const frameZoom = state?.frameZoom ?? FrameZoom.FRAME_ZOOM_NONE;

  const isCompositionActive =
    frameAlign !== FrameAlign.FRAME_ALIGN_NONE || frameZoom !== FrameZoom.FRAME_ZOOM_NONE;

  const safePositionCountdown = 0;
  const unassignedWorkers = Object.entries(
    proxy?.state?.control?.studio?.autoTrackingWorker ?? {}
  ).filter(
    ([id, worker]) =>
      id.length > 0 &&
      worker.state?.status?.readyAt !== undefined &&
      worker.state?.control?.deviceId === undefined
  ).length;

  const hasNoWorkers = !hasAssignment && unassignedWorkers === 0;

  const trackingEngagedDeviceIds = Object.entries(proxy?.state?.control?.device ?? {})
    .filter(([deviceId, _]) => {
      const trackingID = Object.entries(
        proxy?.state?.control?.studio?.autoTrackingWorker ?? {}
      ).find(([_, worker]) => toHexFromId(worker.state?.control?.deviceId) === deviceId)?.[0];

      return trackingID !== undefined && trackingID.length > 0;
    })
    .map(([id, _]) => id);

  const trackingEngagedDeviceNames = trackingEngagedDeviceIds.map((id) => {
    const device = proxy?.state.control?.studio?.device[id];

    return device?.name ?? '';
  });

  const handleRequestTransferTracking = async (): Promise<void> => {
    // upsert the existing reservation
    await proxy?.transferToAutoTrackingReservation(deviceId);
  };

  /*
    const unassignedWorkers = Object.values(proxy?.state?.control?.studio?.autoTrackingWorker ?? {}).filter(
      (worker) => worker.state?.control?.deviceId === undefined
    );
  
    if (unassignedWorkers.length > 0 && hasReservation && !hasAssignment) {
      if (recreateReservationTimeout === undefined) {
        recreateReservationTimeout = setTimeout(async () => {
          try {
            await handleDestroyReservation();
          } catch (e) {
          }
  
          try {
            await handleCreateReservation();
          } catch (e) {
          }
  
          recreateReservationTimeout = undefined;
        }, 15000);
      }
  
    } else {
      clearTimeout(recreateReservationTimeout);
      recreateReservationTimeout = undefined;
    }
  */

  const handleCreateReservation = async (): Promise<boolean> => {
    if (proxy === undefined) {
      return false;
    }

    await proxy?.createAutoTrackingReservation(deviceId);

    return true;
  };

  const handleDestroyReservation = async (_force: boolean = true): Promise<void> => {
    if (proxy === undefined) {
      return;
    }

    await proxy?.destroyAutoTrackingReservation(deviceId);
  };

  const handleDrawBoundingBox = (bbox: number[]): void => {
    const isSet = bbox.length === 4;

    if (!isSet) {
      mutateAutoTrackingState(
        {},
        {
          control: {
            selectionBbox: {
              x1: 1,
              y1: 1,
              x2: 1,
              y2: 1,
              isHuman: true
            }
          }
        }
      );

      return;
    }

    const x1 = bbox[0]!;
    const y1 = bbox[1]!;
    const x2 = bbox[2]!;
    const y2 = bbox[3]!;

    mutateAutoTrackingState({ control: { selectionBbox: { x1, y1, x2, y2, isHuman: false } } }, {});
  };
  return {
    // @ts-ignore
    annotations: filteredAnnotations,
    deadband: state?.deadBand,
    deadbandSafetyArea: true,
    detectionMode,
    hasAnnotations,
    hasAssignment,
    hasNoWorkers,
    hasReservation,
    isConnected: true,
    isAnnotationSelected,
    isCompositionActive,
    isTracking,
    isRestarting: false,
    isRedetecting,
    isReturningToSafePosition,
    wantsTracking,
    safePositionCountdown,
    selectedSubjectId: state?.selectedSubjectId,
    handleChangeToleranceDeadband,
    handleSelectAnnotation,
    handleStopTrackingSelectedSubject,
    handleCreateReservation,
    handleRequestTransferTracking,
    handleDestroyReservation,
    handleDrawBoundingBox,
    trackingEngagedDeviceNames,
    redetectionCountdown: redetectionCountdownSmooth
  };
};
